/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    ReceiptHeaderCustomFieldsService,
    ReceiptHeadersService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { ReceiptHeaderStore } from './receipt-header.store';
import { ReceiptLineStore } from '../receipt-lines/receipt-line.store';
import { InventoryBundleStore } from '../inventory-bundles/inventory-bundle.store';
import { InventoryBundleRead } from '../../entities/inventory-bundle.dto';
import {
    ReceiptHeaderCreate,
    ReceiptHeaderRead,
    ReceiptHeaderSearchParams,
} from '../../entities/receipt-header.dto';
import { ICustomField } from '@pf/shared-common';
import { Operation } from 'fast-json-patch';
import {
    ReceiptLineDto,
    ReceiptLineRead,
} from '../../entities/receipt-line.dto';
import { asArraySafe } from '@pf/shared-utility';
import { ReceiptLineMapper } from '../receipt-lines/receipt-line.mapper';

@Injectable()
export class ReceiptHeaderDataService extends AbstractDataService<
    ReceiptHeaderRead,
    ReceiptHeaderCreate
> {
    private readonly receiptLineStore = inject(ReceiptLineStore);
    private readonly receiptLineMapper = inject(ReceiptLineMapper);
    private readonly inventoryBundleStore = inject(InventoryBundleStore);
    private readonly customFieldsService = inject(
        ReceiptHeaderCustomFieldsService
    );
    protected EntityName = PFWmsEntities.ReceiptHeader;
    constructor(
        private service: ReceiptHeadersService,
        store: ReceiptHeaderStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(
        body: ReceiptHeaderCreate
    ): Observable<ReceiptHeaderRead> {
        return this.service.receiptHeaderCreate({
            receiptHeaderViewModelCreateBody: body,
        }) as Observable<ReceiptHeaderRead>;
    }
    protected deleteObs$(id: string): Observable<ReceiptHeaderRead> {
        return this.service.receiptHeaderDelete({
            id,
        }) as Observable<ReceiptHeaderRead>;
    }
    protected undoDeleteObs$(id: string): Observable<ReceiptHeaderRead> {
        return this.service.receiptHeaderUndoDelete({
            id,
        }) as Observable<ReceiptHeaderRead>;
    }
    protected getObs$(id: string): Observable<ReceiptHeaderRead> {
        return this.service
            .receiptHeaderReadById({
                id,
            })
            .pipe(
                map(header => {
                    this.addLinesToStore(header as ReceiptHeaderRead);
                    return header;
                })
            ) as Observable<ReceiptHeaderRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<ReceiptHeaderRead> {
        return this.service.receiptHeaderUpdatePatch({
            id,
            operation: operations,
        }) as Observable<ReceiptHeaderRead>;
    }
    protected searchObs$(
        searchParams: ReceiptHeaderSearchParams
    ): Observable<IPagedResult<ReceiptHeaderRead>> {
        return this.service.receiptHeaderSearch(searchParams).pipe(
            map(result => result as IPagedResult<ReceiptHeaderRead>),
            tap(result => {
                asArraySafe(result.data).forEach(header => {
                    this.addLinesToStore(header);
                });
            })
        );
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<ReceiptHeaderRead>>> {
        return this.service.receiptHeaderGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<ReceiptHeaderRead>>
        >;
    }

    protected override createCustomFieldObs$(
        entityId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.receiptHeaderCreateCustomField({
            entityId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override updateCustomFieldObs$(
        entityId: string,
        customFieldId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.receiptHeaderCustomFieldsUpdatePut({
            entityId,
            id: customFieldId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override deleteCustomFieldObs$(
        entityId: string,
        customFieldId: string
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.receiptHeaderDeleteCustomField({
            entityId,
            id: customFieldId,
        }) as Observable<ICustomField & { id: string }>;
    }

    private addLinesToStore(header: ReceiptHeaderRead) {
        asArraySafe(header.receiptLines).forEach(line => {
            this.receiptLineStore.add(line as ReceiptLineRead, false);
        });
    }

    receiveLines$(
        headerId: string,
        lineIds: string[],
        receivingContainerId: string
    ) {
        return this.service
            .receiptHeaderReceiveMultiple({
                receiptHeaderId: headerId,
                receiptHeaderReceiveLinesViewModel: {
                    inventoryReceiptLineIds: lineIds,
                    containerId: receivingContainerId,
                },
            })
            .pipe(
                map(receivedLines => {
                    if (!receivedLines.length) {
                        return [];
                    }
                    this.receiptLineStore.upsertMany(
                        receivedLines as ReceiptLineRead[]
                    );
                    const bundles = (receivedLines as ReceiptLineRead[])
                        .map(line => line.receivedBundle)
                        .filter(
                            (b): b is InventoryBundleRead => !!b?.id
                        );
                    if (bundles.length) {
                        bundles.forEach(b =>
                            this.inventoryBundleStore.update(b)
                        );
                    }
                    return this.receiptLineMapper.toDTOList(
                        receivedLines as ReceiptLineRead[]
                    );
                })
            ) as Observable<ReceiptLineDto[]>;
    }
}
